import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Clock, Calendar, Users, MonitorPlay, Play } from 'lucide-react';
import { tmdbApi, getPosterUrl, getBackdropUrl, formatRuntime, formatReleaseDate, formatVoteAverage } from '../api/tmdb';

const buildVidsrcUrl = (tmdbId, mediaType = 'movie') => {
    const isTV = mediaType === 'tv';
    return isTV
        ? `https://vidsrc.me/embed/tv?tmdb=${tmdbId}`
        : `https://vidsrc.me/embed/movie?tmdb=${tmdbId}`;
};

const MovieDetailModal = ({ movieId, mediaType = 'movie', seedItem, onClose }) => {
    const isTV = mediaType === 'tv';
    const { data: item } = useQuery({
        queryKey: [mediaType, movieId],
        queryFn: () => (mediaType === 'tv' ? tmdbApi.getTVDetails(movieId) : tmdbApi.getMovieDetails(movieId)),
        enabled: !!movieId,
        staleTime: 1000 * 60 * 10,
    });

    const { data: credits } = useQuery({
        queryKey: [mediaType + 'Credits', movieId],
        queryFn: () => (mediaType === 'tv' ? tmdbApi.getTVCredits(movieId) : tmdbApi.getMovieCredits(movieId)),
        enabled: !!movieId,
        staleTime: 1000 * 60 * 10,
    });

    // Regional ratings
    const { data: movieReleases } = useQuery({
        queryKey: ['movieReleaseDates', movieId],
        queryFn: () => tmdbApi.getMovieReleaseDates(movieId),
        enabled: mediaType === 'movie' && !!movieId,
        staleTime: 1000 * 60 * 30,
    });
    const { data: tvRatings } = useQuery({
        queryKey: ['tvContentRatings', movieId],
        queryFn: () => tmdbApi.getTVContentRatings(movieId),
        enabled: mediaType === 'tv' && !!movieId,
        staleTime: 1000 * 60 * 30,
    });

    const cast = credits?.cast?.slice(0, 12) || [];
    const embedUrl = useMemo(() => buildVidsrcUrl(movieId, mediaType), [movieId, mediaType]);

    // For TV episodes, fetch first season by default
    const [selectedSeason, setSelectedSeason] = useState(1);
    const { data: seasonData } = useQuery({
        queryKey: ['tvSeason', movieId, selectedSeason],
        queryFn: () => tmdbApi.getTVSeason(movieId, selectedSeason),
        enabled: isTV && !!movieId,
        staleTime: 1000 * 60 * 10,
    });

    const base = item || seedItem || {};
    const displayTitle = base.title || base.name || base.original_title || base.original_name || 'Untitled';
    const displayDate = base.release_date || base.first_air_date || '';
    const displayRuntime = base.runtime || (base.episode_run_time && base.episode_run_time[0]);

    const country = 'US';
    const movieCert = (movieReleases?.results || [])
        .find(r => r.iso_3166_1 === country)?.release_dates?.find(rd => rd.certification)?.certification || '';
    const tvCert = (tvRatings?.results || [])
        .find(r => r.iso_3166_1 === country)?.rating || '';
    const certification = mediaType === 'movie' ? movieCert : tvCert;
    const languagesCount = (base.spoken_languages || []).length || (base.languages || []).length || 0;

    const handleWatchNow = () => {
        const url = buildVidsrcUrl(movieId, mediaType);
        window.dispatchEvent(new CustomEvent('open-player', {
            detail: {
                source: url,
                title: displayTitle,
                mediaType,
                tmdbId: movieId,
            }
        }));
        if (onClose) onClose();
    };

    const handlePlayEpisode = (seasonNumber, episodeNumber) => {
        const episodeUrl = `https://vidsrc.me/embed/tv?tmdb=${movieId}&season=${seasonNumber}&episode=${episodeNumber}`;
        window.dispatchEvent(new CustomEvent('open-player', {
            detail: {
                source: episodeUrl,
                title: `${displayTitle} • S${seasonNumber}E${episodeNumber}`,
                mediaType: 'tv',
                season: seasonNumber,
                episode: episodeNumber,
                tmdbId: movieId,
            }
        }));
        if (onClose) onClose();
    };

    return (
        <AnimatePresence>
            {movieId && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center p-4"
                >
                    {/* Backdrop with liquid glass vibe */}
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-md" />

                    {/* Content */}
                    <motion.div
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 20, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="relative z-[110] w-full max-w-[720px] h-auto rounded-3xl border border-white/15 bg-white/10 shadow-2xl overflow-hidden"
                    >
                        {/* Close */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 rounded-full bg-black/40 hover:bg-black/60 text-white z-[120]"
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>
                        {/* Compact glass card layout */}
                        <div className="relative z-[115] p-4 sm:p-5">
                            {/* Header row: language, Mute icon placeholder, etc. */}
                            <div className="flex items-center justify-between mb-3">
                                <span className="px-2.5 py-1 rounded-md bg-white/15 text-xs text-white/90 border border-white/20">
                                    {base.original_language?.toUpperCase() || 'EN'}
                                </span>
                            </div>

                            {/* Poster backdrop with overlay and title */}
                            <div className="relative rounded-lg overflow-hidden">
                                {base.backdrop_path && (
                                    <img
                                        src={getBackdropUrl(base.backdrop_path, 'w780')}
                                        alt={displayTitle}
                                        className="w-full h-60 object-cover opacity-80"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                                {base.poster_path && (
                                    <img
                                        src={getPosterUrl(base.poster_path, 'w342')}
                                        alt={displayTitle}
                                        className="absolute left-3 -bottom-6 w-16 h-24 object-cover rounded-lg bottom-2 border border-white/20 shadow-xl"
                                    />
                                )}
                                <div className="absolute left-20 bottom-2 right-2">
                                    <div className="text-lg font-semibold line-clamp-1">{displayTitle}</div>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="mt-6 flex items-center gap-3">
                                <button onClick={handleWatchNow} className="flex-1 rounded-xl bg-white text-black font-medium py-2.5 text-sm inline-flex items-center justify-center gap-2">
                                    <Play size={16} /> Watch Now
                                </button>
                            </div>

                            {/* Meta row */}
                            <div className="mt-3 text-white/80 text-sm flex items-center flex-wrap gap-x-3 gap-y-1">
                                {displayDate && <span>{new Date(displayDate).getFullYear()}</span>}
                                {mediaType === 'tv' && base.number_of_seasons ? (
                                    <span>{base.number_of_seasons} Season{base.number_of_seasons > 1 ? 's' : ''}</span>
                                ) : (
                                    displayRuntime ? <span>{formatRuntime(displayRuntime)}</span> : null
                                )}
                                {certification && <span>{certification}</span>}
                                {languagesCount > 0 && <span>{languagesCount} {languagesCount === 1 ? 'Language' : 'Languages'}</span>}
                            </div>

                            {/* Overview */}
                            {base.overview ? (
                                <p className="mt-2 text-white/80 text-sm leading-snug ">{base.overview}</p>
                            ) : null}

                            {/* If TV: Quick episodes list (first few) */}
                            {isTV && (seasonData?.episodes?.length > 0) && (
                                <div className="mt-4 space-y-2 py-3 pe-3 my-2 overflow-y-auto max-h-[300px]">
                                    {(seasonData.episodes || []).slice(0, 5).map(ep => (
                                        <button
                                            key={ep.id}
                                            onClick={() => handlePlayEpisode(selectedSeason, ep.episode_number)}
                                            className="w-full flex items-center gap-3 p-2.5 rounded-xl border border-white/15 bg-white/5 text-left"
                                        >
                                            <div className="w-10 h-6 rounded bg-white/10 text-[11px] flex items-center justify-center">
                                                E{ep.episode_number}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="text-white text-sm truncate">{ep.name || `Episode ${ep.episode_number}`}</p>
                                                <p className="text-white/60 text-xs truncate">{ep.overview}</p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default MovieDetailModal;
